/* eslint-disable react/no-danger */
/* eslint-disable no-unused-vars */
import { Box, Button, CircularProgress, TextField } from '@mui/material'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import Popper from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import ControlPointIcon from '@mui/icons-material/ControlPoint'
import ClearIcon from '@mui/icons-material/Clear'
import { toast } from 'react-toastify'
import useStyles from '../style'
import IOSSwitch from '../../Form/IOSSwitch'
import { changeProductCategories, productCategory, productCategoryTree } from '../../../api/product'
import { getAccountID } from '../../../utils/auth'
import { TOAST_DEFAULT_CONFIG } from '../../../constants/toast'
import { errorParserRequest } from '../../../utils/errorParser'
import { highlightTextV2 } from '../../../utils/highlight-text'
import { useOnClickOutside } from '../../../utils/hooks/useClickOutside'
import { categoryMapper } from '../utils/category-map'
import PlusIcon from '../../Icons/PlusIcon'
import Spinner from '../../Spinner'

const changedKey = {
  book: 'book_productCampaign_allSuperCampaigns_samePlatform',
  block: 'block_productCampaign_allSuperCampaigns_samePlatform',
  ignore: 'ignore_productCampaign_allSuperCampaigns_samePlatform',
}

const newItemDefaultInfo = {
  searchPhrase: '',
  category: {
    categoryID: '',
    label: '',
  },
  book_productCampaign: false,
  book_productCampaign_allSuperCampaigns_samePlatform: false,
  block_productCampaign: false,
  block_productCampaign_allSuperCampaigns_samePlatform: false,
  ignore_productCampaign: false,
  ignore_productCampaign_allSuperCampaigns_samePlatform: false,
  conflict: false,
  book: false,
  block: false,
  ignore: false,
  isNewCategory: true,
  isFocused: false,
}

const PLATFORM = {
  BOL: 'Bol',
}
const LIMIT = {
  Bol: 100,
}

const Categories = ({ productData, setShowWarning = () => {} }) => {
  const classes = useStyles()

  const [list, setList] = useState([])
  const [newCategoryList, setNewCategoryList] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [availableCategory, setAvailableCategory] = useState([])
  const [isAvailableCategoryLoading, setIsAvailableCategoryLoading] = useState(true)
  const [searchPhrase, setSearchPhrase] = useState('')
  const [triggerNewItemPos, setTriggerNewItemPos] = useState(-1)

  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const dropdownRef = useRef()

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
    setOpen((previousOpen) => !previousOpen)
  }

  const canBeOpen = open && Boolean(anchorEl)
  const id = canBeOpen ? 'spring-popper' : undefined

  const isChangesExist = useMemo(
    () =>
      list.filter(
        (item) =>
          item.book !== item[changedKey.book] ||
          item.block !== item[changedKey.block] ||
          item.ignore !== item[changedKey.ignore]
      ).length,
    [list]
  )

  const isNewCategoryExist = useMemo(
    () => newCategoryList.filter((item) => item.searchPhrase).length,
    [newCategoryList]
  )

  const showCountWarning = useMemo(() => {
    const combined = list.concat(newCategoryList)
    const count = combined.filter((item) => item.book || item.block).length

    return count > LIMIT[PLATFORM.BOL]
  }, [list, newCategoryList])

  const onChangeExistCategoriesHandler = (value, key, index) => {
    list[index].book = false
    list[index].block = false
    list[index].ignore = false
    list[index][key] = value
    setList([...list])
  }

  const onChangeNewCategoryHandler = (value, key, index) => {
    if (key !== 'searchPhrase') {
      newCategoryList[index].book = false
      newCategoryList[index].block = false
      newCategoryList[index].ignore = false
    }
    newCategoryList[index][key] = value
    setNewCategoryList([...newCategoryList])
  }

  const getData = async () => {
    setIsLoading(true)

    try {
      const { productID, channelID } = productData
      const res = await productCategory(getAccountID(), channelID, productID)

      const mapped = res.data?.map((item) => ({
        ...item,
        searchPhrase: item?.category?.label,
        book: item[changedKey.book],
        block: item[changedKey.block],
        ignore: item[changedKey.ignore],
      }))

      setList(mapped || [])
      setIsLoading(false)
    } catch (err) {
      toast.error(errorParserRequest(err), TOAST_DEFAULT_CONFIG)
      setIsLoading(false)
    }
  }

  const getAvailableCategory = async () => {
    setIsAvailableCategoryLoading(true)

    try {
      const { productID, channelID } = productData
      const res = await productCategoryTree(getAccountID(), channelID, productID)
      const mapped = categoryMapper(res?.data)
      setAvailableCategory(mapped)
    } catch (err) {
      toast.error(errorParserRequest(err), TOAST_DEFAULT_CONFIG)
    } finally {
      setIsAvailableCategoryLoading(false)
    }
  }

  const saveChanges = async () => {
    setIsUpdating(true)
    try {
      const { productID, channelID } = productData

      const payload = list
        .filter(
          (item) =>
            item.book !== item[changedKey.book] ||
            item.block !== item[changedKey.block] ||
            item.ignore !== item[changedKey.ignore]
        )
        .concat(newCategoryList)
        .map((item) => ({
          productID,
          categoryStatusData: {
            category: {
              label: item.category.label,
              categoryID: item.category.categoryID,
            },
            book_productCampaign: item.book_productCampaign,
            book_productCampaign_allSuperCampaigns_samePlatform: item.book,
            block_productCampaign: item.block_productCampaign,
            block_productCampaign_allSuperCampaigns_samePlatform: item.block,
            ignore_productCampaign: item.ignore_productCampaign,
            ignore_productCampaign_allSuperCampaigns_samePlatform: item.ignore,
            conflict: item.conflict,
          },
        }))

      try {
        const res = await changeProductCategories(getAccountID(), channelID, payload)
        if (res.status === 200 || res.status === 201) {
          toast.success(`${res?.data?.Response} `, TOAST_DEFAULT_CONFIG)
          getData()
          setNewCategoryList([])
        }
      } catch (err) {
        toast.error(`${errorParserRequest(err)}`, TOAST_DEFAULT_CONFIG)
      }
    } catch (err) {
      toast.error(errorParserRequest(err), TOAST_DEFAULT_CONFIG)
      setIsUpdating(false)
    } finally {
      setIsUpdating(false)
    }
  }

  const getRow = (item, index, onChangeHandler) => (
    <Box className={[classes.row, classes.category_row].join(' ')}>
      {item.isNewCategory ? (
        <div className={classes.new_keyword}>
          <TextField
            fullWidth
            type="text"
            placeholder="Enter Category"
            value={item.searchPhrase}
            onFocus={(e) => {
              handleClick(e)
              setTriggerNewItemPos(index)
            }}
            onBlur={() => {
              // setOpen(false)
            }}
            onChange={(e) => {
              if (!open) handleClick(e)
              setSearchPhrase(e.target.value)
              onChangeHandler(e.target.value, 'searchPhrase', index)
            }}
            onClick={(e) => {
              if (!open) {
                handleClick(e)
                setTriggerNewItemPos(index)
              }
            }}
          />
          <ClearIcon
            sx={{
              width: 20,
              height: 20,
              cursor: 'pointer',
            }}
            onClick={() => setNewCategoryList((prev) => prev.filter((val, i) => i !== index))}
          />
        </div>
      ) : (
        <span title={item.searchPhrase}>{item.searchPhrase}</span>
      )}
      <span>
        <IOSSwitch
          checked={Boolean(item.book)}
          onChange={(e) => {
            onChangeHandler(e.target.checked, 'book', index)
          }}
        />
      </span>
      <span>
        <IOSSwitch
          checked={Boolean(item.block)}
          onChange={(e) => {
            onChangeHandler(e.target.checked, 'block', index)
          }}
        />
      </span>
      <span>
        <IOSSwitch
          checked={Boolean(item.ignore)}
          onChange={(e) => {
            onChangeHandler(e.target.checked, 'ignore', index)
          }}
        />
      </span>
    </Box>
  )

  const filteredCategories = useMemo(() => {
    if (!searchPhrase) return availableCategory
    return availableCategory.filter((item) => item.label.toLowerCase().includes(searchPhrase.toLowerCase()))
  }, [availableCategory, searchPhrase])

  useOnClickOutside(dropdownRef, (e) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen(false)
  })

  useEffect(() => {
    if (open && !availableCategory.length) {
      getAvailableCategory()
    }
    if (!open) {
      setTriggerNewItemPos(-1)
    }
  }, [open])

  useEffect(() => {
    if (showCountWarning) {
      setShowWarning(true)
    } else {
      setShowWarning(false)
    }
  }, [showCountWarning])

  useEffect(() => {
    if (productData) {
      getData()
    }
  }, [productData])

  useEffect(
    () => () => {
      setNewCategoryList([])
    },
    []
  )

  return (
    <>
      <Box className={classes.wrapper_body}>
        <Box className={classes.body_table_header}>
          <Box className={classes.row}>
            <span>Category</span>
            <span>Book</span>
            <span>Block</span>
            <span>Ignore</span>
          </Box>
        </Box>
        <Box className={classes.body_table}>
          {!isLoading && newCategoryList.map((item, index) => getRow(item, index, onChangeNewCategoryHandler))}
          {!isLoading && list.map((item, index) => getRow(item, index, onChangeExistCategoriesHandler))}
          {(isLoading || isUpdating) && <Spinner />}
          {!list.length && !isLoading && <div className={classes.empty_msg}>No categories found</div>}
        </Box>
      </Box>
      <Box className={classes.wrapper_footer}>
        <Button
          variant="outlined"
          color="secondary"
          className={classes.create}
          onClick={() => setNewCategoryList((prev) => [{ ...newItemDefaultInfo }, ...prev])}
        >
          <PlusIcon /> Category
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!isChangesExist && !isNewCategoryExist}
          className={classes.save_btn}
          onClick={saveChanges}
        >
          Save changes
        </Button>
      </Box>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        style={{
          zIndex: 111111,
        }}
        transition
        placement="bottom-start"
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <div className={classes.categories_dropdown} ref={dropdownRef}>
              <div className={classes.categories_dropdown_inner}>
                {isAvailableCategoryLoading && (
                  <div className={classes.category_item}>
                    <CircularProgress
                      sx={{
                        position: 'absolute',
                        left: '50%',
                      }}
                    />
                  </div>
                )}
                {!isAvailableCategoryLoading &&
                  filteredCategories.map((item) => (
                    <div className={classes.category_item} key={item.categoryID}>
                      <div
                        className={classes.category_text}
                        dangerouslySetInnerHTML={{
                          __html: highlightTextV2(item.label, searchPhrase),
                        }}
                      />
                      <button
                        className={classes.add_button}
                        type="button"
                        onClick={() => {
                          setNewCategoryList((prev) =>
                            prev.map((val, i) => {
                              if (i === triggerNewItemPos) {
                                return {
                                  ...val,
                                  searchPhrase: item.label,
                                  category: item,
                                }
                              }

                              return val
                            })
                          )
                          setOpen(false)
                          setSearchPhrase('')
                        }}
                      >
                        +
                      </button>
                    </div>
                  ))}
                {!isAvailableCategoryLoading && !filteredCategories.length && (
                  <div className={classes.category_item}>
                    <span className={`${classes.category_text} ${classes.center}`}>Sorry, no result found</span>
                  </div>
                )}
              </div>
            </div>
          </Fade>
        )}
      </Popper>
    </>
  )
}

export default memo(Categories)
